import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Loader2, Film, Play, Pause, Repeat, Check, AlertTriangle, Flag, Eraser, Mic, Volume2, Music, Plus, Sparkles, Download, FolderOpen, ImagePlus, Clapperboard, Wand2, Palette, Layers, Cloud, MapPin, Sun, PenLine, Scissors, Undo2, Redo2, ChevronDown, SlidersHorizontal } from "lucide-react";
import { getReel, setFrameReview, generateReelVoice, getReelVoiceStatus, getReelAudioUrl } from "@/services/shotService";
import { getReelEdit, putReelEdit, uploadReelAsset, generateReelMusic, startReelRender, getReelRenderStatus, getReelRenderDownloadUrl, uploadReelVisual, getReelAssetUrl, animateShot, getAnimateStatus, startVfx, getVfxStatus, getVfxProviders, getVfxJobs } from "@/services/editService";
import { buildEdlFromReel, applyGroupOrder, insertVisualClip, groupIndexAtTime, splitVideoGroupAt } from "./reelTiming";
import { ReelEngine } from "./reelEngine";
import { VideoPool, syncVideo, drawContain, decodeMeta } from "./reelVideo";
import { MARK_COLORS } from "./DirectorTimeline";
import MultiTrackTimeline from "./MultiTrackTimeline";
import ClipInspector from "./ClipInspector";
import OverlayInspector from "./OverlayInspector";
import OverlayHandles from "./OverlayHandles";
import ExportsPanel from "./ExportsPanel";
import MediaPool from "./MediaPool";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const MARK_BUTTONS = [
  { status: "approved", label: "Approve", Icon: Check },
  { status: "needs-work", label: "Needs work", Icon: AlertTriangle },
  { status: "flagged", label: "Flag", Icon: Flag },
  { status: null, label: "Clear", Icon: Eraser },
];
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// VFX "what do you want to change?" tiles. Each wraps the user's text in a guided,
// subject-PRESERVING sentence (pre + text + suf) so they only type the CHANGE — the
// model keeps the real subject instead of regenerating it. `pre`/`suf` MUST match the
// backend vfx_providers._MODE so the live "Sends:" read-back equals what's actually sent.
const VFX_MODES = [
  { key: "look", label: "Look", Icon: Palette, ph: "1970s film, warm grain  ·  cyberpunk anime  ·  noir B&W",
    pre: "Restyle the whole shot to look like ", suf: " — keep the same people, faces and motion." },
  { key: "background", label: "Background", Icon: Layers, ph: "a blurred neon city street at night",
    pre: "Replace only the background behind them with ", suf: ". Keep the subject exactly as filmed." },
  { key: "sky", label: "Sky", Icon: Cloud, ph: "dramatic storm clouds at golden hour",
    pre: "Replace only the sky with ", suf: ". Keep the people and everything below the horizon unchanged." },
  { key: "place", label: "Place", Icon: MapPin, ph: "a sunlit field of lavender with snow mountains",
    pre: "Put them in ", suf: ", keeping the same subject, clothing and motion unchanged." },
  { key: "light", label: "Lighting", Icon: Sun, ph: "warm sunset glow from the left, long shadows",
    pre: "Relight the scene like ", suf: " — change only the light and mood, keep everyone in place." },
  // Free write — your prompt is sent RAW, exactly as typed (no scaffold). Advanced.
  { key: "free", label: "Free write", Icon: PenLine, ph: "write the whole instruction yourself — describe the change, not the subject",
    pre: "", suf: "" },
];

// Transition library (maps 1:1 to ffmpeg xfade types — render.py validates against
// its allow-list). Stored on a shot's `transition_in` ({type,duration}); the export
// chains them duration-preservingly so audio/subtitle timing never drifts.
const TRANSITIONS = [
  { type: "dissolve", label: "Dissolve" }, { type: "fade", label: "Crossfade" },
  { type: "fadeblack", label: "Dip to black" }, { type: "fadewhite", label: "Dip to white" },
  { type: "wipeleft", label: "Wipe ◀" }, { type: "wiperight", label: "Wipe ▶" },
  { type: "wipeup", label: "Wipe ▲" }, { type: "wipedown", label: "Wipe ▼" },
  { type: "slideleft", label: "Slide ◀" }, { type: "slideright", label: "Slide ▶" },
  { type: "slideup", label: "Slide ▲" }, { type: "slidedown", label: "Slide ▼" },
  { type: "circleopen", label: "Circle open" }, { type: "circleclose", label: "Circle close" },
  { type: "radial", label: "Radial" }, { type: "pixelize", label: "Pixelize" },
  { type: "zoomin", label: "Zoom in" }, { type: "smoothright", label: "Smooth ▶" },
  { type: "coverleft", label: "Cover ◀" }, { type: "revealright", label: "Reveal ▶" },
  { type: "squeezev", label: "Squeeze" }, { type: "fadegrays", label: "Fade grays" },
];

/** Our own license-free reel editor: canvas frame compositor driven by the
 *  Web-Audio clock, multi-track movable audio stems, subtitles, marks, VO. */
export default function ReelEditor({ open, onClose, projectId }) {
  const [reel, setReel] = useState(null);
  const [edl, setEdl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [selectedShot, setSelectedShot] = useState(null);
  const [selectedClipId, setSelectedClipId] = useState(null);
  const [marks, setMarks] = useState({});
  const [past, setPast] = useState([]);     // undo stack (prior EDLs)
  const [future, setFuture] = useState([]); // redo stack
  const edlRef = useRef(null);              // always-current committed EDL (for history snapshots)
  const dragBaseRef = useRef(null);         // pre-drag EDL snapshot, captured on the first live move
  const [generating, setGenerating] = useState(false);
  const [voiceProgress, setVoiceProgress] = useState(null);
  const [musicOpen, setMusicOpen] = useState(false);
  const [musicPrompt, setMusicPrompt] = useState("");
  const [musicQuality, setMusicQuality] = useState("clip");
  const [musicBusy, setMusicBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [exportsRefresh, setExportsRefresh] = useState(0);
  const [binOpen, setBinOpen] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState(null); // groupKey whose incoming transition is being picked
  const [stageDrop, setStageDrop] = useState(false);
  const [animating, setAnimating] = useState(() => new Set());
  const [vfxTarget, setVfxTarget] = useState(null); // { groupKey, plateUrl, frameId, seconds } | null
  const [vfxMode, setVfxMode] = useState("look");
  const [vfxPrompt, setVfxPrompt] = useState("");
  const [vfxBusy, setVfxBusy] = useState(false);
  const [vfxModel, setVfxModel] = useState(null);   // the SELECTED VFX model
  const [vfxModels, setVfxModels] = useState([]);   // all models from /vfx/providers
  const renderPollRef = useRef(null);
  const animatePollRef = useRef(null);
  const vfxPollRef = useRef(null);

  const engineRef = useRef(null);
  const canvasRef = useRef(null);
  const imgCacheRef = useRef(new Map());
  const videoPoolRef = useRef(null);
  const playingRef = useRef(false);
  const drawFrameRef = useRef(() => {});
  const saveTimer = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const addKindRef = useRef("music");
  const pollRef = useRef(null);

  const fps = reel?.fps || 30;
  const videoClips = useMemo(() => edl?.tracks.find((t) => t.kind === "video")?.clips || [], [edl]);
  const overlayClips = useMemo(() => (edl?.tracks || []).filter((t) => t.kind === "overlay").flatMap((t) => t.clips), [edl]);
  const subtitles = useMemo(() => edl?.subtitles || [], [edl]);

  // ---- load reel + merge saved EDL ----
  const loadReel = useCallback(async () => {
    const d = await getReel(projectId);
    setReel(d);
    const fresh = buildEdlFromReel(d);
    let merged = fresh;
    try {
      const saved = (await getReelEdit(projectId))?.edl;
      if (saved?.tracks) {
        const freshVideo = fresh.tracks.find((t) => t.kind === "video");
        const savedVideo = saved.tracks.find((t) => t.kind === "video");
        // re-inject DROPPED visual clips (no frame_id) — they live only in the EDL,
        // so they'd be lost when the video track is rebuilt from the storyboard.
        // (Animated shots also have asset_key but keep their frame_id → rebuilt fresh.)
        const assetClips = (savedVideo?.clips || []).filter((c) => c.asset_key && !c.frame_id);
        const mergedVideo = assetClips.length ? { ...freshVideo, clips: [...freshVideo.clips, ...assetClips] } : freshVideo;
        const savedAudio = saved.tracks.filter((t) => t.kind !== "video");
        const hasVo = savedAudio.some((t) => t.kind === "vo");
        const freshVo = fresh.tracks.find((t) => t.kind === "vo");
        merged = { ...fresh, tracks: [mergedVideo, ...(hasVo ? savedAudio : [freshVo, ...savedAudio])], subtitles: saved.subtitles || fresh.subtitles };
        // re-apply a saved manual shot order (also repacks re-injected assets cleanly)
        if (Array.isArray(saved.order) && saved.order.length) merged = applyGroupOrder(merged, saved.order);
        else if (assetClips.length) merged = applyGroupOrder(merged, []);
      }
    } catch { /* no saved edit */ }
    setEdl(merged);
    const m = {};
    (d.shots || []).forEach((s) => { if (s.review_status || s.director_notes) m[s.frame_id] = { review_status: s.review_status, director_notes: s.director_notes }; });
    setMarks(m);
    return d;
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setError(null); setPlayhead(0); setPlaying(false); setSelectedShot(null); setSelectedClipId(null);
    loadReel().then(() => { if (!cancelled) setLoading(false); })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.detail || e.message); setLoading(false); } });
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); if (renderPollRef.current) clearInterval(renderPollRef.current); if (animatePollRef.current) clearInterval(animatePollRef.current); if (vfxPollRef.current) clearInterval(vfxPollRef.current); if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [open, loadReel]);

  // ---- canvas drawing (kept current via ref so the engine always calls latest) ----
  const drawFrame = useCallback((t) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const cw = canvas.width, ch = canvas.height;
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch);
    const pool = videoPoolRef.current;
    const playing = playingRef.current;
    const redraw = () => drawFrameRef.current(engineRef.current?.currentTime() ?? 0);
    const activeVideoIds = new Set();

    // ---- base clip (still image or video). A transition_in fades the shot up over
    //      its window (preview approximation; the exact xfade renders in export). ----
    const clip = videoClips.find((c) => t >= c.start && t < c.start + c.duration) || videoClips[videoClips.length - 1];
    if (clip) {
      const tin = clip.transition_in;
      const ta = (tin && tin.duration) ? Math.max(0, Math.min(1, (t - clip.start) / tin.duration)) : 1;
      ctx.save(); ctx.globalAlpha = ta;
      if (clip.mediaType === "video" && pool) {
        const vid = pool.get(clip, redraw);
        activeVideoIds.add(clip.id);
        const sp = clip.speed || 1;
        if (vid.playbackRate !== sp) { try { vid.playbackRate = sp; } catch { /* ignore */ } }
        syncVideo(vid, (t - clip.start) * sp + (clip.in || 0), playing);
        if (vid.readyState >= 2 && vid.videoWidth) drawContain(ctx, vid, vid.videoWidth, vid.videoHeight, cw, ch, 1);
      } else {
        const img = imgCacheRef.current.get(clip.frame_id || clip.id);
        if (img && img.complete && img.naturalWidth) {
          // static hold — matches the export (the old 4% preview zoom never rendered).
          drawContain(ctx, img, img.naturalWidth, img.naturalHeight, cw, ch, 1);
        }
      }
      ctx.restore();
    }

    // ---- overlays (image or video) composited on top ----
    for (const ov of overlayClips) {
      if (ov.disabled || t < ov.start || t >= ov.start + ov.duration) continue;
      let source = null, sw = 0, sh = 0;
      if (ov.mediaType === "video" && pool) {
        const vid = pool.get(ov, redraw);
        activeVideoIds.add(ov.id);
        syncVideo(vid, t - ov.start + (ov.in || 0), playing);
        if (vid.readyState >= 2 && vid.videoWidth) { source = vid; sw = vid.videoWidth; sh = vid.videoHeight; }
      } else {
        const oimg = imgCacheRef.current.get(ov.id);
        if (oimg && oimg.complete && oimg.naturalWidth) { source = oimg; sw = oimg.naturalWidth; sh = oimg.naturalHeight; }
      }
      if (!source) continue;
      const ow = (ov.w ?? 0.5) * cw;
      const oh = ow / (sw / sh);
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, ov.opacity ?? 1));
      ctx.drawImage(source, (ov.x ?? 0.5) * cw - ow / 2, (ov.y ?? 0.5) * ch - oh / 2, ow, oh);
      ctx.restore();
    }

    // ---- reel-level fade from/to black (preview; exact in export) ----
    const fi = edl?.fade_in || 0, fo = edl?.fade_out || 0;
    if (fi > 0 || fo > 0) {
      const dur = engineRef.current?.duration || edl?.totalSec || 0;
      let fa = 0;
      if (fi > 0 && t < fi) fa = 1 - t / fi;
      if (fo > 0 && dur > 0 && t > dur - fo) fa = Math.max(fa, (t - (dur - fo)) / fo);
      if (fa > 0.001) { ctx.save(); ctx.globalAlpha = Math.min(1, fa); ctx.fillStyle = "#000"; ctx.fillRect(0, 0, cw, ch); ctx.restore(); }
    }

    if (pool) pool.pauseExcept(activeVideoIds);
  }, [videoClips, overlayClips, edl]);
  useEffect(() => { drawFrameRef.current = drawFrame; }, [drawFrame]);
  useEffect(() => { playingRef.current = playing; if (!playing) videoPoolRef.current?.pauseAll(); }, [playing]);

  // preload images
  useEffect(() => {
    if (!edl) return;
    const cache = imgCacheRef.current;
    [...videoClips, ...overlayClips].filter((c) => c.mediaType !== "video").forEach((c) => {
      const key = c.frame_id || c.id;
      if (cache.has(key)) return;
      const img = new Image();
      img.crossOrigin = null;
      img.onload = () => drawFrameRef.current(engineRef.current?.currentTime() ?? 0);
      img.src = c.src;
      cache.set(key, img);
    });
    // drop pooled <video>s whose clips are gone
    videoPoolRef.current?.prune(new Set([...videoClips, ...overlayClips].filter((c) => c.mediaType === "video").map((c) => c.id)));
  }, [edl, videoClips, overlayClips]);

  // create engine ONCE per open (guard), then apply edits via update()
  useEffect(() => {
    if (!edl || engineRef.current) return;
    if (!videoPoolRef.current) videoPoolRef.current = new VideoPool();
    const engine = new ReelEngine(edl, {
      onTime: (t) => { setPlayhead(t); drawFrameRef.current(t); },
      onPlayStateChange: setPlaying,
    });
    engineRef.current = engine;
    engine.load().then(() => drawFrameRef.current(0));
  }, [edl]);
  useEffect(() => () => {
    engineRef.current?.dispose(); engineRef.current = null;
    videoPoolRef.current?.dispose(); videoPoolRef.current = null;
    imgCacheRef.current = new Map();
  }, []);

  // apply EDL edits to the engine
  useEffect(() => {
    const e = engineRef.current;
    if (!e || !edl) return;
    e.update(edl);
    e.ensureBuffers().then(() => drawFrameRef.current(e.currentTime()));
  }, [edl]);

  const currentSub = useMemo(() => subtitles.find((s) => playhead >= s.start && playhead < s.start + s.duration), [subtitles, playhead]);

  // ---- mutations + autosave ----
  const scheduleSave = useCallback((next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { putReelEdit(projectId, next).catch(() => {}); }, 800);
  }, [projectId]);

  // ---- undo / redo (history of committed EDLs) ----
  useEffect(() => { edlRef.current = edl; }, [edl]);   // live ref → snapshot pre-mutation state
  const histPush = useCallback((snapshot) => {
    if (!snapshot) return;
    setPast((p) => [...p.slice(-49), snapshot]);
    setFuture([]);
  }, []);
  const undo = useCallback(() => {
    setPast((p) => {
      if (!p.length) return p;
      const prev = p[p.length - 1];
      setFuture((f) => [edlRef.current, ...f].slice(0, 50));
      setSelectedClipId(null);
      setEdl(prev); scheduleSave(prev);
      return p.slice(0, -1);
    });
  }, [scheduleSave]);
  const redo = useCallback(() => {
    setFuture((f) => {
      if (!f.length) return f;
      const nxt = f[0];
      setPast((p) => [...p.slice(-49), edlRef.current]);
      setSelectedClipId(null);
      setEdl(nxt); scheduleSave(nxt);
      return f.slice(1);
    });
  }, [scheduleSave]);

  // The selected clip — declared before the handlers/effects that list it in deps (TDZ-safe).
  const selectedClip = useMemo(() => {
    if (!selectedClipId || !edl) return null;
    for (const t of edl.tracks) { const c = t.clips.find((x) => x.id === selectedClipId); if (c) return { clip: c, trackId: t.id, trackKind: t.kind }; }
    return null;
  }, [selectedClipId, edl]);

  const changeClip = useCallback((trackId, clipId, patch, commit) => {
    if (!commit && !dragBaseRef.current) dragBaseRef.current = edlRef.current;  // snapshot pre-drag
    setEdl((prev) => {
      const next = { ...prev, tracks: prev.tracks.map((t) => t.id !== trackId ? t : { ...t, clips: t.clips.map((c) => c.id !== clipId ? c : { ...c, ...patch }) }) };
      if (commit) scheduleSave(next);
      return next;
    });
    if (commit) { const base = dragBaseRef.current; dragBaseRef.current = null; histPush(base); }
  }, [scheduleSave, histPush]);

  const changeTrack = useCallback((trackId, patch) => {
    setEdl((prev) => {
      const next = { ...prev, tracks: prev.tracks.map((t) => t.id !== trackId ? t : { ...t, ...patch }) };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const removeClip = useCallback((trackId, clipId) => {
    histPush(edlRef.current);
    setSelectedClipId(null);
    setEdl((prev) => {
      const next = { ...prev, tracks: prev.tracks.map((t) => t.id !== trackId ? t : { ...t, clips: t.clips.filter((c) => c.id !== clipId) }) };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave, histPush]);

  const reorderShots = useCallback((newOrderKeys) => {
    histPush(edlRef.current);
    setEdl((prev) => {
      const next = applyGroupOrder(prev, newOrderKeys);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave, histPush]);

  // ---- blade/split at playhead + drag-trim a video shot's edges ----
  const splitAtPlayhead = useCallback(() => {
    setEdl((prev) => {
      const next = splitVideoGroupAt(prev, playhead);
      if (next === prev) return prev;        // nothing splittable under the playhead
      histPush(prev);
      scheduleSave(next);
      return next;
    });
  }, [playhead, scheduleSave, histPush]);

  const trimVideoGroup = useCallback((groupKey, patch, commit) => {
    if (!commit && !dragBaseRef.current) dragBaseRef.current = edlRef.current;
    setEdl((prev) => {
      const tracks = prev.tracks.map((t) => t.kind !== "video" ? t : { ...t, clips: t.clips.map((c) => (c.group_key || c.frame_id) === groupKey ? { ...c, ...patch } : c) });
      const next = applyGroupOrder({ ...prev, tracks }, []);
      if (commit) scheduleSave(next);
      return next;
    });
    if (commit) { const base = dragBaseRef.current; dragBaseRef.current = null; histPush(base); }
  }, [scheduleSave, histPush]);

  /** Drop/insert an image or video as a new shot at time `atSec` (defaults to playhead). */
  const addVisualAtTime = useCallback(async (file, atSec) => {
    if (!file || !/^(image|video)\//.test(file.type || "")) { toast.error("Drop an image or video file"); return; }
    const tid = toast.loading(`Adding ${file.name}…`);
    try {
      const meta = await decodeMeta(file);
      const { key } = await uploadReelVisual(projectId, file, { width: meta.width, height: meta.height, duration: meta.duration });
      const clip = { id: `vis-${key}`, asset_key: key, mediaType: meta.isVideo ? "video" : "image", src: getReelAssetUrl(key),
        start: 0, duration: meta.isVideo ? Math.max(0.5, meta.duration || 3) : 3, in: 0,
        group_key: `VIS-${key}`, poster: meta.poster || null, meta: { label: file.name, type: meta.isVideo ? "video" : "image" } };
      setEdl((prev) => {
        const idx = groupIndexAtTime(prev, fps, atSec ?? playhead);
        const next = insertVisualClip(prev, clip, idx);
        scheduleSave(next);
        return next;
      });
      toast.success(`Inserted ${file.name}`, { id: tid });
    } catch (e) {
      toast.error("Could not add clip", { id: tid, description: e?.response?.data?.detail || e.message });
    }
  }, [projectId, fps, playhead, scheduleSave]);

  /** Set/clear the transition ENTERING a shot (stored on the group's first video clip). */
  const setTransition = useCallback((groupKey, transition) => {
    histPush(edlRef.current);
    setEdl((prev) => {
      const vt = prev.tracks.find((t) => t.kind === "video");
      const first = vt?.clips.find((c) => (c.group_key || c.frame_id) === groupKey);
      if (!first) return prev;
      const tracks = prev.tracks.map((t) => t.kind !== "video" ? t : { ...t, clips: t.clips.map((c) => c === first ? { ...c, transition_in: transition || null } : c) });
      const next = { ...prev, tracks }; scheduleSave(next); return next;
    });
  }, [scheduleSave, histPush]);

  /** Master output volume (0–1.5). */
  const setMasterGain = useCallback((v) => {
    setEdl((prev) => { const next = { ...prev, master_gain: v }; scheduleSave(next); return next; });
  }, [scheduleSave]);

  /** Reel-level fade from/to black (seconds). which = "in" | "out". */
  const setReelFade = useCallback((which, val) => {
    histPush(edlRef.current);
    setEdl((prev) => {
      const next = { ...prev, [which === "in" ? "fade_in" : "fade_out"]: val || 0 };
      scheduleSave(next); return next;
    });
  }, [scheduleSave, histPush]);

  /** Duplicate the selected clip (audio/overlay) or the selected shot, placed right after. */
  const duplicateSelected = useCallback(() => {
    const u = () => Math.random().toString(36).slice(2, 6);
    if (selectedClip) {
      histPush(edlRef.current);
      setEdl((prev) => {
        const tracks = prev.tracks.map((t) => {
          if (t.id !== selectedClip.trackId) return t;
          const c = t.clips.find((x) => x.id === selectedClip.clip.id);
          if (!c) return t;
          const s = u();
          const clone = { ...c, id: `${c.id}-d${s}`, start: (c.start || 0) + (c.duration || 1), group_key: c.group_key ? `${c.group_key}-d${s}` : undefined };
          return { ...t, clips: [...t.clips, clone] };
        });
        const next = { ...prev, tracks }; scheduleSave(next); return next;
      });
    } else if (selectedShot) {
      histPush(edlRef.current);
      setEdl((prev) => {
        const vt = prev.tracks.find((t) => t.kind === "video"); if (!vt) return prev;
        const gk = selectedShot.group_key;
        const idxs = vt.clips.map((c, i) => ((c.group_key || c.frame_id) === gk ? i : -1)).filter((i) => i >= 0);
        if (!idxs.length) return prev;
        const s = u(); const newKey = `${gk}-d${s}`;
        const clones = idxs.map((i, k) => ({ ...vt.clips[i], id: `${vt.clips[i].id}-d${s}`, group_key: newKey, transition_in: k === 0 ? null : vt.clips[i].transition_in }));
        const after = idxs[idxs.length - 1] + 1;
        const newClips = [...vt.clips.slice(0, after), ...clones, ...vt.clips.slice(after)];
        const tracks = prev.tracks.map((t) => t === vt ? { ...t, clips: newClips } : t);
        const next = applyGroupOrder({ ...prev, tracks }, []); scheduleSave(next); return next;
      });
    }
  }, [selectedClip, selectedShot, scheduleSave, histPush]);

  /** Playback speed of a video shot (s>1 faster, s<1 slow-mo). Timeline length unchanged. */
  const setShotSpeed = useCallback((groupKey, speed) => {
    histPush(edlRef.current);
    setEdl((prev) => {
      const tracks = prev.tracks.map((t) => t.kind !== "video" ? t : { ...t, clips: t.clips.map((c) => (c.group_key || c.frame_id) === groupKey ? { ...c, speed } : c) });
      const next = { ...prev, tracks }; scheduleSave(next); return next;
    });
  }, [scheduleSave, histPush]);

  /** In/out range for a partial export. which = "in" | "out"; clears with null. */
  const setRange = useCallback((which, val) => {
    setEdl((prev) => {
      const next = { ...prev, [which === "in" ? "range_in" : "range_out"]: val };
      scheduleSave(next); return next;
    });
  }, [scheduleSave]);

  /** Drop/remove a timeline marker at the playhead (M). */
  const toggleMarker = useCallback(() => {
    setEdl((prev) => {
      const at = engineRef.current?.currentTime?.() ?? playhead;
      const cur = prev.markers || [];
      const near = cur.find((mk) => Math.abs(mk - at) < 0.15);
      const markers = near != null ? cur.filter((mk) => mk !== near) : [...cur, at].sort((a, b) => a - b);
      const next = { ...prev, markers }; scheduleSave(next); return next;
    });
  }, [playhead, scheduleSave]);

  /** Insert a media-bin asset as a new shot at `atSec` — re-uses the upload (no re-upload). */
  const addVisualFromAsset = useCallback((asset, atSec) => {
    if (!asset?.key) return;
    const isVideo = asset.kind === "video" || /^video\//.test(asset.mime || "");
    const u = Math.random().toString(36).slice(2, 7);
    histPush(edlRef.current);
    const clip = { id: `vis-${asset.key}-${u}`, asset_key: asset.key, mediaType: isVideo ? "video" : "image", src: getReelAssetUrl(asset.key),
      start: 0, duration: isVideo ? Math.max(0.5, asset.duration || 3) : 3, in: 0,
      group_key: `VIS-${asset.key}-${u}`, poster: null, meta: { label: asset.filename || asset.key, type: isVideo ? "video" : "image" } };
    setEdl((prev) => {
      const idx = groupIndexAtTime(prev, fps, atSec ?? playhead);
      const next = insertVisualClip(prev, clip, idx);
      scheduleSave(next);
      return next;
    });
    toast.success(`Added ${asset.filename || "clip"}`);
  }, [fps, playhead, scheduleSave, histPush]);

  const setVisualDuration = useCallback((groupKey, dur) => {
    setEdl((prev) => {
      const d = Math.max(0.3, dur || 0.3);
      const tracks = prev.tracks.map((t) => t.kind !== "video" ? t : { ...t, clips: t.clips.map((c) => c.group_key === groupKey ? { ...c, duration: d } : c) });
      const next = applyGroupOrder({ ...prev, tracks }, prev.order || []);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const removeShot = useCallback((groupKey) => {
    histPush(edlRef.current);
    setSelectedShot(null);
    setEdl((prev) => {
      const tracks = prev.tracks.map((t) => t.kind !== "video" ? t : { ...t, clips: t.clips.filter((c) => (c.group_key || c.frame_id) !== groupKey) });
      const order = (prev.order || []).filter((k) => k !== groupKey);
      const next = applyGroupOrder({ ...prev, tracks }, order);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave, histPush]);

  /** Swap a shot-group's video clip to point at a new asset (the VFX result). */
  const replaceGroupMedia = useCallback((groupKey, key) => {
    setEdl((prev) => {
      const src = getReelAssetUrl(key);
      const tracks = prev.tracks.map((t) => t.kind !== "video" ? t : {
        ...t, clips: t.clips.map((c) => c.group_key === groupKey
          ? { ...c, id: `vfx-${key}`, asset_key: key, src, mediaType: "video", poster: null } : c),
      });
      const next = { ...prev, tracks };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const handleStartVfx = useCallback(async () => {
    if (!vfxTarget || !vfxPrompt.trim()) return;
    const target = vfxTarget;                 // capture before we close the modal
    setVfxBusy(true);
    const tid = toast.loading("Rendering VFX shot…", { description: "Runs in the background — keep editing." });
    try {
      const { job_id } = await startVfx(projectId, { plate_url: target.plateUrl, prompt: vfxPrompt.trim(), mode: vfxMode, provider: vfxModel?.name || undefined, frame_id: target.frameId || undefined, group_key: target.groupKey });
      setVfxBusy(false); setVfxTarget(null); setVfxPrompt("");   // close the modal → render in the background
      if (vfxPollRef.current) clearInterval(vfxPollRef.current);
      vfxPollRef.current = setInterval(async () => {
        try {
          const st = await getVfxStatus(job_id);
          if (st.status === "done" && st.asset_key) {
            clearInterval(vfxPollRef.current); vfxPollRef.current = null;
            replaceGroupMedia(target.groupKey, st.asset_key);
            toast.success("VFX shot ready — on the timeline", { id: tid });
          } else if (st.status === "failed") {
            clearInterval(vfxPollRef.current); vfxPollRef.current = null;
            toast.error("VFX failed", { id: tid, description: (st.error || "").slice(0, 160) });
          }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) {
      setVfxBusy(false);
      toast.error("Could not start VFX", { id: tid, description: e?.response?.data?.detail || e.message });
    }
  }, [vfxTarget, vfxPrompt, vfxMode, vfxModel, projectId, replaceGroupMedia]);

  // Resume / re-link VFX renders after the editor was closed mid-render. Runs once per
  // open: re-applies a render that finished while away, and resumes any still in flight.
  const vfxResumeRanRef = useRef(false);
  const resumePollsRef = useRef([]);
  useEffect(() => { if (!open) { vfxResumeRanRef.current = false; resumePollsRef.current.forEach(clearInterval); resumePollsRef.current = []; } }, [open]);
  useEffect(() => {
    if (!open || !edl || loading || vfxResumeRanRef.current) return;
    vfxResumeRanRef.current = true;
    let cancelled = false;
    getVfxJobs(projectId).then(({ jobs }) => {
      if (cancelled || !jobs) return;
      const vt = edl.tracks.find((t) => t.kind === "video");
      const assetOf = (gk) => vt?.clips.find((c) => (c.group_key || c.frame_id) === gk)?.asset_key;
      const age = (iso) => (iso ? (Date.now() - Date.parse(iso)) / 60000 : 999);
      jobs.forEach((j) => {
        if (!j.group_key) return;
        if (j.status === "done" && j.asset_key && age(j.updated_at) < 60) {
          if (assetOf(j.group_key) !== j.asset_key) { replaceGroupMedia(j.group_key, j.asset_key); toast.message("Re-applied a VFX render that finished earlier"); }
        } else if (["rendering", "queued", "finalizing"].includes(j.status) && age(j.updated_at) < 20) {
          const tid = toast.loading("Resuming a VFX render…");
          const iv = setInterval(async () => {
            try {
              const st = await getVfxStatus(j.id);
              if (st.status === "done" && st.asset_key) { clearInterval(iv); replaceGroupMedia(j.group_key, st.asset_key); toast.success("VFX shot ready — on the timeline", { id: tid }); }
              else if (st.status === "failed") { clearInterval(iv); toast.error("VFX failed", { id: tid, description: (st.error || "").slice(0, 160) }); }
            } catch { /* keep polling */ }
          }, 5000);
          resumePollsRef.current.push(iv);
        }
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, edl, loading, projectId, replaceGroupMedia]);
  useEffect(() => () => { resumePollsRef.current.forEach(clearInterval); resumePollsRef.current = []; }, []);

  // Load the active VFX model (label, $/sec, clip limits) for the modal — data-driven
  // so swapping/adding models server-side needs no frontend change.
  useEffect(() => {
    let cancelled = false;
    getVfxProviders()
      .then((res) => {
        if (cancelled) return;
        const list = res?.providers || [];
        setVfxModels(list);
        const avail = list.filter((p) => p.available);
        // prefer the server default if its key is configured, else first available
        setVfxModel(avail.find((p) => p.name === res?.default) || avail[0] || list[0] || null);
      })
      .catch(() => { /* modal falls back to generic copy */ });
    return () => { cancelled = true; };
  }, []);

  // Selected VFX mode's guided scaffold — drives the placeholder + the "Sends:" read-back.
  const vfxDef = VFX_MODES.find((m) => m.key === vfxMode) || VFX_MODES[0];

  /** Drop an image or video as an OVERLAY composited over the video at (xFrac,yFrac). */
  const addOverlay = useCallback(async (file, xFrac = 0.5, yFrac = 0.5) => {
    if (!file || !/^(image|video)\//.test(file.type || "")) { toast.error("Drop an image or video to overlay"); return; }
    const tid = toast.loading(`Adding overlay…`);
    try {
      const meta = await decodeMeta(file);
      const { key } = await uploadReelVisual(projectId, file, { width: meta.width, height: meta.height, duration: meta.duration });
      const clip = { id: `ov-${key}`, asset_key: key, mediaType: meta.isVideo ? "video" : "image", src: getReelAssetUrl(key),
        start: Math.max(0, playhead), duration: meta.isVideo ? Math.max(0.5, meta.duration || 3) : 3, in: 0,
        x: xFrac, y: yFrac, w: 0.5, opacity: 1, poster: meta.poster || null, label: file.name };
      setEdl((prev) => {
        const hasOverlay = prev.tracks.some((t) => t.kind === "overlay");
        const tracks = hasOverlay
          ? prev.tracks.map((t) => t.kind === "overlay" ? { ...t, clips: [...t.clips, clip] } : t)
          : [...prev.tracks, { id: "overlay", kind: "overlay", clips: [clip] }];
        const next = { ...prev, tracks };
        scheduleSave(next);
        return next;
      });
      setSelectedShot(null); setSelectedClipId(clip.id);
      toast.success("Overlay added — adjust size/opacity in the inspector", { id: tid });
    } catch (e) {
      toast.error("Could not add overlay", { id: tid, description: e?.response?.data?.detail || e.message });
    }
  }, [projectId, playhead, scheduleSave]);

  /** Add an empty stacked video layer (overlay track) — composites over the base AND exports. */
  const addVideoLayer = useCallback(() => {
    histPush(edlRef.current);
    setEdl((prev) => {
      const next = { ...prev, tracks: [...prev.tracks, { id: `overlay-${Math.random().toString(36).slice(2, 6)}`, kind: "overlay", clips: [] }] };
      scheduleSave(next);
      return next;
    });
    toast.success("Added a video layer — drop a clip onto it");
  }, [scheduleSave, histPush]);

  const removeLayer = useCallback((trackId) => {
    histPush(edlRef.current);
    setSelectedClipId(null);
    setEdl((prev) => { const next = { ...prev, tracks: prev.tracks.filter((t) => t.id !== trackId) }; scheduleSave(next); return next; });
  }, [scheduleSave, histPush]);

  /** Add an empty audio track (Voice/Music/SFX) you can drop clips onto. Unique id so
   *  multiple tracks of the same kind are allowed (music, music-2…). */
  const addEmptyAudioTrack = useCallback((kind) => {
    histPush(edlRef.current);
    setEdl((prev) => {
      let id = kind, n = 2;
      while (prev.tracks.some((t) => t.id === id)) { id = `${kind}-${n}`; n += 1; }
      const next = { ...prev, tracks: [...prev.tracks, { id, kind, gain: 1, clips: [] }] };
      scheduleSave(next); return next;
    });
  }, [scheduleSave, histPush]);

  /** Place media on a specific video layer. payload = { file } (uploads) or { asset } (re-uses a bin key). */
  const addToLayer = useCallback(async (trackId, payload, atSec) => {
    let key, isVideo, dur, poster = null, label;
    try {
      if (payload.asset) {
        key = payload.asset.key; isVideo = payload.asset.kind === "video"; dur = payload.asset.duration; label = payload.asset.filename;
      } else if (payload.file) {
        const meta = await decodeMeta(payload.file);
        const up = await uploadReelVisual(projectId, payload.file, { width: meta.width, height: meta.height, duration: meta.duration });
        key = up.key; isVideo = meta.isVideo; dur = meta.duration; poster = meta.poster || null; label = payload.file.name;
      } else return;
    } catch (e) { toast.error("Could not add to layer", { description: e?.response?.data?.detail || e.message }); return; }
    histPush(edlRef.current);
    const clip = { id: `ov-${key}-${Math.random().toString(36).slice(2, 5)}`, asset_key: key, mediaType: isVideo ? "video" : "image", src: getReelAssetUrl(key),
      start: Math.max(0, atSec ?? playhead), duration: isVideo ? Math.max(0.5, dur || 3) : 3, in: 0,
      x: 0.5, y: 0.5, w: 0.5, opacity: 1, poster, label };
    setEdl((prev) => {
      const tracks = prev.tracks.map((t) => t.id === trackId ? { ...t, clips: [...t.clips, clip] } : t);
      const next = { ...prev, tracks }; scheduleSave(next); return next;
    });
    setSelectedShot(null); setSelectedClipId(clip.id);
    toast.success("Added to layer — size/opacity in the inspector");
  }, [projectId, playhead, scheduleSave, histPush]);

  /** Push a decoded audio clip onto a track (creating the track if needed) + autosave.
   *  `src` is persisted in the EDL (absolute reel-audio URL) so it survives a reload;
   *  `buf`, if supplied, is injected so it plays/shows its waveform immediately. */
  const insertAudioClip = useCallback(({ clipId, src, buf, duration, kind, label, group_key, gain = 1, fade_in = 0, fade_out = 0 }) => {
    const eng = engineRef.current;
    if (buf && eng) eng.buffers[clipId] = buf;
    const clip = { id: clipId, src, group_key, start: Math.max(0, playhead), duration, gain, fade_in, fade_out, label };
    setEdl((prev) => {
      const tracks = prev.tracks.map((t) => ({ ...t, clips: [...t.clips] }));
      let ti = tracks.findIndex((t) => t.kind === kind);
      if (ti === -1) { tracks.push({ id: kind, kind, gain: 1, clips: [] }); ti = tracks.length - 1; }
      tracks[ti].clips.push(clip);
      const next = { ...prev, tracks };
      scheduleSave(next);
      return next;
    });
    setSelectedClipId(clipId);
  }, [playhead, scheduleSave]);

  const addAudioFile = useCallback(async (file, kind) => {
    const eng = engineRef.current;
    if (!eng) return;
    try {
      if (!eng.ctx) await eng.load();
      const buf = await eng.ctx.decodeAudioData(await file.arrayBuffer());
      const { key } = await uploadReelAsset(projectId, file);
      insertAudioClip({ clipId: `a-${key}`, src: getReelAudioUrl(key), buf, duration: buf.duration, kind, label: file.name, group_key: key });
      toast.success(`Added ${kind}: ${file.name}`);
    } catch (e) {
      toast.error("Could not add audio", { description: e?.response?.data?.detail || e.message });
    }
  }, [projectId, insertAudioClip]);

  const handleGenerateMusic = useCallback(async () => {
    const prompt = musicPrompt.trim();
    if (!prompt) return;
    setMusicBusy(true);
    try {
      const { key, seconds } = await generateReelMusic(projectId, prompt, musicQuality);
      const src = getReelAudioUrl(key);
      const eng = engineRef.current;
      let buf = null, dur = seconds || 30;
      try {
        if (eng) {
          if (!eng.ctx) await eng.load();
          const resp = await fetch(src);
          buf = await eng.ctx.decodeAudioData(await resp.arrayBuffer());
          dur = buf.duration;
        }
      } catch { /* engine.ensureBuffers will lazy-load on the next edl effect */ }
      const label = `♪ ${prompt.length > 24 ? prompt.slice(0, 24) + "…" : prompt}`;
      // Seat the score under the VO by default (duckable) + ease it in/out.
      insertAudioClip({ clipId: `a-${key}`, src, buf, duration: dur, kind: "music", label, group_key: key,
        gain: 0.4, fade_in: 1.5, fade_out: 2 });
      setMusicOpen(false); setMusicPrompt("");
      toast.success("Music added — trim, fade and duck it under the VO");
    } catch (e) {
      toast.error("Could not generate music", { description: e?.response?.data?.detail || e.message });
    } finally { setMusicBusy(false); }
  }, [musicPrompt, musicQuality, projectId, insertAudioClip]);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    const t = toast.loading("Rendering MP4 — runs server-side (~30–90s)");
    try {
      const { job_id } = await startReelRender(projectId, edl); // send live EDL (beats the autosave debounce)
      setExportsOpen(true);                       // surface it in the Exports tab
      setExportsRefresh((x) => x + 1);
      if (renderPollRef.current) clearInterval(renderPollRef.current);
      renderPollRef.current = setInterval(async () => {
        try {
          const st = await getReelRenderStatus(job_id);
          if (st.status === "done") {
            clearInterval(renderPollRef.current); renderPollRef.current = null;
            setExporting(false);
            setExportsRefresh((x) => x + 1);
            const a = document.createElement("a");     // auto-download the fresh one
            a.href = getReelRenderDownloadUrl(job_id); a.rel = "noopener";
            document.body.appendChild(a); a.click(); a.remove();
            toast.success(`MP4 ready — ${(((st.bytes || 0) / 1e6)).toFixed(1)}MB · ${Math.round(st.seconds || 0)}s · saved to Exports`, { id: t });
          } else if (st.status === "failed") {
            clearInterval(renderPollRef.current); renderPollRef.current = null;
            setExporting(false);
            setExportsRefresh((x) => x + 1);
            toast.error("Render failed", { id: t, description: (st.error || "").slice(0, 200) });
          }
        } catch { /* transient — keep polling */ }
      }, 2500);
    } catch (e) {
      setExporting(false);
      toast.error("Could not start export", { id: t, description: e?.response?.data?.detail || e.message });
    }
  }, [exporting, projectId, edl]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) addAudioFile(f, addKindRef.current);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const triggerAdd = (kind) => { addKindRef.current = kind; fileInputRef.current?.click(); };

  const handleSelectShot = useCallback((group) => {
    setSelectedShot(group); setSelectedClipId(null);
    engineRef.current?.seek(group.startFrame / fps);
  }, [fps]);

  const saveMark = useCallback(async (frameId, payload) => {
    setMarks((prev) => ({ ...prev, [frameId]: { ...prev[frameId], ...payload } }));
    try { await setFrameReview(frameId, payload); } catch (e) { toast.error("Could not save mark"); }
  }, []);

  const voGroups = useMemo(() => (edl?.groups || []).filter((g) => g.vo_text).length, [edl]);
  const hasBakedAudio = useMemo(() => (reel?.shots || []).some((s) => s.has_audio), [reel]);
  const animatedFrames = useMemo(() => {
    const s = new Set();
    (reel?.shots || []).forEach((sh) => { if (sh.animation?.status === "done") s.add(sh.frame_id); });
    return s;
  }, [reel]);
  const animatingRef = useRef(animating);
  useEffect(() => { animatingRef.current = animating; }, [animating]);

  const pollAnimations = useCallback(() => {
    if (animatePollRef.current) return;
    animatePollRef.current = setInterval(async () => {
      if (!animatingRef.current.size) { clearInterval(animatePollRef.current); animatePollRef.current = null; return; }
      try {
        const list = await getAnimateStatus(projectId);
        const byId = Object.fromEntries(list.map((a) => [a.frame_id, a]));
        const done = [], failed = [];
        for (const fid of animatingRef.current) {
          const st = byId[fid]?.status;
          if (st === "done") done.push(fid);
          else if (st === "failed") failed.push([fid, byId[fid]?.error]);
        }
        if (done.length || failed.length) {
          setAnimating((prev) => { const n = new Set(prev); [...done, ...failed.map((f) => f[0])].forEach((fid) => n.delete(fid)); return n; });
          failed.forEach(([, err]) => toast.error("Animation failed", { description: (err || "").slice(0, 160) }));
          if (done.length) { await loadReel(); toast.success(`${done.length} shot${done.length > 1 ? "s" : ""} animated`); }
        }
      } catch { /* keep polling */ }
    }, 5000);
  }, [projectId, loadReel]);

  // On load (and after a recycle), resume polling any shots still 'rendering' so the
  // backend sweeper's recovered animations appear without a manual reload.
  useEffect(() => {
    const rendering = (reel?.shots || []).filter((sh) => sh.animation?.status === "rendering").map((sh) => sh.frame_id).filter(Boolean);
    if (rendering.length) {
      setAnimating((prev) => { const n = new Set(prev); rendering.forEach((id) => n.add(id)); return n; });
      pollAnimations();
    }
  }, [reel, pollAnimations]);

  const handleAnimateShot = useCallback(async (frameId) => {
    if (!frameId || animatingRef.current.has(frameId)) return;
    setAnimating((prev) => new Set(prev).add(frameId));
    try {
      await animateShot(projectId, frameId);
      pollAnimations();
      toast.message("Animating shot…", { description: "Google Veo is generating a moving clip (~1–2 min)" });
    } catch (e) {
      setAnimating((prev) => { const n = new Set(prev); n.delete(frameId); return n; });
      toast.error("Could not start animation", { description: e?.response?.data?.detail || e.message });
    }
  }, [projectId, pollAnimations]);

  const handleAnimateAll = useCallback(async () => {
    const seen = new Set();
    const targets = (reel?.shots || []).filter((sh) => {
      if (seen.has(sh.group_key)) return false; seen.add(sh.group_key);            // one per shot-group
      return sh.animation?.status !== "done" && !animatingRef.current.has(sh.frame_id);
    });
    if (!targets.length) { toast.message("Every shot is already animated"); return; }
    if (!window.confirm(`Animate ${targets.length} shot(s)? This generates a video per shot — a few minutes and provider credits.`)) return;
    setAnimating((prev) => { const n = new Set(prev); targets.forEach((t) => n.add(t.frame_id)); return n; });
    pollAnimations();
    toast.message(`Animating ${targets.length} shots (a few at a time)…`);
    // Throttled worker pool — never dogpile the provider. Each worker submits a shot,
    // waits for it to leave the 'animating' set (done/failed via pollAnimations), then
    // takes the next. So at most MAX render concurrently.
    const MAX = 4;
    const queue = [...targets];
    const worker = async () => {
      while (queue.length) {
        const t = queue.shift();
        try { await animateShot(projectId, t.frame_id); } catch { /* keep going */ }
        await new Promise((res) => {
          const iv = setInterval(() => { if (!animatingRef.current.has(t.frame_id)) { clearInterval(iv); res(); } }, 3000);
          setTimeout(() => { clearInterval(iv); res(); }, 6 * 60 * 1000);   // safety cap
        });
      }
    };
    await Promise.all(Array.from({ length: MAX }, () => worker()));
  }, [reel, projectId, pollAnimations]);

  const handleGenerateVoice = useCallback(async () => {
    setGenerating(true); setVoiceProgress({ done: 0, total: voGroups });
    try {
      await generateReelVoice(projectId);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const st = await getReelVoiceStatus(projectId);
          const done = st.done || 0;
          setVoiceProgress({ done, total: voGroups });
          if (st.status === "done" || done >= voGroups) {
            clearInterval(pollRef.current); pollRef.current = null;
            await loadReel(); setGenerating(false); toast.success("Voice-over ready");
          } else if (st.status === "failed") {
            clearInterval(pollRef.current); pollRef.current = null; setGenerating(false); toast.error("Voice-over failed");
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) { setGenerating(false); toast.error("Could not start voice-over", { description: e?.response?.data?.detail || e.message }); }
  }, [projectId, voGroups, loadReel]);

  // natural aspect of a selected overlay (for the on-canvas resize box)
  const overlayAspect = useMemo(() => {
    if (selectedClip?.trackKind !== "overlay") return 16 / 9;
    const c = selectedClip.clip;
    const img = imgCacheRef.current.get(c.id);
    if (img?.naturalWidth) return img.naturalWidth / img.naturalHeight;
    const vid = videoPoolRef.current?.els?.get(c.id);
    if (vid?.videoWidth) return vid.videoWidth / vid.videoHeight;
    return 16 / 9;
  }, [selectedClip, playhead]);

  // Keyboard transport + edit shortcuts (ignored while typing in a field).
  useEffect(() => {
    if (!open) return undefined;
    const isField = (t) => t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
    const onKey = (e) => {
      if (isField(e.target)) return;
      const eng = engineRef.current;
      const t = eng?.currentTime?.() ?? playhead;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "z" || e.key === "Z")) { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if (mod && (e.key === "y" || e.key === "Y")) { e.preventDefault(); redo(); }
      else if (mod && (e.key === "d" || e.key === "D")) { e.preventDefault(); duplicateSelected(); }
      else if (mod) return;                                  // leave other Cmd/Ctrl combos to the browser
      else if (e.key === " ") { e.preventDefault(); eng?.toggle(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); eng?.seek(t - 1 / fps); }
      else if (e.key === "ArrowRight") { e.preventDefault(); eng?.seek(t + 1 / fps); }
      else if (e.key === "Home") { e.preventDefault(); eng?.seek(0); }
      else if (e.key === "End") { e.preventDefault(); eng?.seek(eng?.duration || 0); }
      else if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedClip) { e.preventDefault(); removeClip(selectedClip.trackId, selectedClip.clip.id); }
        else if (selectedShot) { e.preventDefault(); removeShot(selectedShot.group_key); }
      }
      else if (e.key === "b" || e.key === "B") { e.preventDefault(); splitAtPlayhead(); }
      else if (e.key === "i" || e.key === "I") { e.preventDefault(); setRange("in", t); }
      else if (e.key === "o" || e.key === "O") { e.preventDefault(); setRange("out", t); }
      else if (e.key === "m" || e.key === "M") { e.preventDefault(); toggleMarker(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, fps, playhead, selectedClip, selectedShot, removeClip, removeShot, splitAtPlayhead, undo, redo, duplicateSelected, setRange, toggleMarker]);

  const lead = selectedShot?.lead || (selectedShot?.frames && selectedShot.frames[0]);
  const selMark = lead ? (marks[lead.frame_id] || {}) : {};
  const duration = engineRef.current?.duration || edl?.totalSec || 0;
  // live (edl-derived) duration for a selected dropped image, so the input stays current
  const selAssetDur = useMemo(() => {
    if (!selectedShot || lead?.mediaType !== "image" || !edl) return 3;
    const cs = (edl.tracks.find((t) => t.kind === "video")?.clips || []).filter((c) => c.group_key === selectedShot.group_key);
    return cs.reduce((a, c) => a + (c.duration || 0), 0) || 3;
  }, [selectedShot, lead, edl]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex flex-col"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          {/* header — calm, near-monochrome; one accent (Export). Actions grouped into menus. */}
          <div className="flex items-center gap-1.5 px-5 py-2.5 border-b border-white/10">
            <Film size={16} className="text-white/40" />
            <div className="flex-1 min-w-0 ml-1">
              <h2 className="text-sm font-semibold text-white leading-none">Reel Editor</h2>
              {reel && <p className="text-[11px] text-white/40 mt-0.5">{reel.shot_count} shots · {Math.round(reel.total_seconds)}s</p>}
            </div>

            <button onClick={() => setBinOpen((v) => !v)} title="Media bin" className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm ${binOpen ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}><FolderOpen size={15} /> Media</button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5"><Plus size={15} /> Add <ChevronDown size={13} className="opacity-50" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#1c1c1f] border-white/10 text-white/80">
                <DropdownMenuItem onClick={() => imageInputRef.current?.click()}><ImagePlus size={14} className="mr-2" /> Image / video…</DropdownMenuItem>
                <DropdownMenuItem onClick={addVideoLayer}><Layers size={14} className="mr-2" /> Video layer</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5">
                  {(animating.size > 0 || generating) ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={15} />} Generate <ChevronDown size={13} className="opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#1c1c1f] border-white/10 text-white/80">
                <DropdownMenuItem onClick={() => setMusicOpen(true)}><Sparkles size={14} className="mr-2" /> Music (AI)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => triggerAdd("music")}><Music size={14} className="mr-2" /> Music (upload)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => triggerAdd("sfx")}><Plus size={14} className="mr-2" /> SFX (upload)</DropdownMenuItem>
                <DropdownMenuItem onClick={handleGenerateVoice} disabled={generating || voGroups === 0}>
                  {hasBakedAudio ? <Volume2 size={14} className="mr-2" /> : <Mic size={14} className="mr-2" />}
                  {generating ? `Voicing ${voiceProgress?.done ?? 0}/${voiceProgress?.total ?? voGroups}…` : hasBakedAudio ? "Re-voice" : "Voice-over"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleAnimateAll} disabled={!reel || videoClips.length === 0}>
                  <Clapperboard size={14} className="mr-2" />{animating.size > 0 ? `Animating ${animating.size}…` : "Previs (animate shots)"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Popover>
              <PopoverTrigger asChild>
                <button type="button" title="Reel settings (fades)" className="flex items-center px-2 py-1.5 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5"><SlidersHorizontal size={15} /></button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-60 bg-[#1c1c1f] border-white/10 text-white/80 space-y-3">
                <div className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">Reel</div>
                <label className="block text-[11px] text-white/60">Fade in from black <span className="float-right text-white/40">{(edl?.fade_in || 0).toFixed(1)}s</span>
                  <input type="range" min={0} max={2} step={0.1} value={edl?.fade_in || 0} onChange={(e) => setReelFade("in", parseFloat(e.target.value))} className="w-full accent-[var(--color-gold-500)] mt-1" />
                </label>
                <label className="block text-[11px] text-white/60">Fade out to black <span className="float-right text-white/40">{(edl?.fade_out || 0).toFixed(1)}s</span>
                  <input type="range" min={0} max={2} step={0.1} value={edl?.fade_out || 0} onChange={(e) => setReelFade("out", parseFloat(e.target.value))} className="w-full accent-[var(--color-gold-500)] mt-1" />
                </label>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" disabled={exporting || !edl || videoClips.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-gold-500)] text-black text-sm font-medium hover:bg-[var(--color-gold-400)] disabled:opacity-40">
                  {exporting ? <><Loader2 size={14} className="animate-spin" /> Exporting…</> : <><Download size={14} /> Export</>} <ChevronDown size={13} className="opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#1c1c1f] border-white/10 text-white/80">
                <DropdownMenuItem onClick={handleExport} disabled={exporting}><Download size={14} className="mr-2" /> Render MP4{(edl?.range_in || edl?.range_out != null) ? " (range only)" : ""}</DropdownMenuItem>
                {(edl?.range_in || edl?.range_out != null) && <DropdownMenuItem onClick={() => { setRange("in", 0); setRange("out", null); }}>Clear export range</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => setExportsOpen(true)}><FolderOpen size={14} className="mr-2" /> Past exports…</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button onClick={onClose} className="p-1.5 ml-0.5 text-white/50 hover:text-white"><X size={18} /></button>
            <input ref={fileInputRef} type="file" accept="audio/*" onChange={onPickFile} className="hidden" />
            <input ref={imageInputRef} type="file" accept="image/*,video/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) addVisualAtTime(f, playhead); if (imageInputRef.current) imageInputRef.current.value = ""; }} className="hidden" />
          </div>

          {/* stage */}
          <div className="flex-1 flex items-center justify-center px-6 min-h-0">
            {loading ? <Loader2 className="animate-spin text-[var(--color-gold-400)]" size={28} />
              : error ? <p className="text-white/60 text-sm">Could not build reel: {error}</p>
              : !edl || videoClips.length === 0 ? <p className="text-white/60 text-sm">No rendered shots yet — render some frames first.</p>
              : (
                <div className={`relative ${stageDrop ? "ring-2 ring-violet-400/80 rounded-lg" : ""}`} style={{ width: "100%", maxWidth: "min(100%, calc((100vh - 380px) * 1.7778))", aspectRatio: "16 / 9" }}
                  onDragOver={(e) => { e.preventDefault(); if (!stageDrop) setStageDrop(true); }}
                  onDragLeave={(e) => { if (e.currentTarget === e.target) setStageDrop(false); }}
                  onDrop={(e) => {
                    e.preventDefault(); setStageDrop(false);
                    const f = e.dataTransfer?.files?.[0]; if (!f) return;
                    const rect = canvasRef.current.getBoundingClientRect();
                    const xf = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                    const yf = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
                    addOverlay(f, xf, yf);
                  }}>
                  <canvas ref={canvasRef} width={1280} height={720} onClick={() => engineRef.current?.toggle()} className="w-full h-full rounded-lg bg-black cursor-pointer" />
                  {selectedClip?.trackKind === "overlay" && (
                    <OverlayHandles clip={selectedClip.clip} aspect={overlayAspect} trackId={selectedClip.trackId} onChange={changeClip} />
                  )}
                  {stageDrop && <div className="absolute inset-0 flex items-center justify-center bg-violet-500/10 rounded-lg pointer-events-none text-violet-200 text-sm font-medium">Drop image/video to overlay here</div>}
                  {currentSub && (
                    <div className="absolute inset-x-0 bottom-[8%] flex flex-col items-center px-[8%] pointer-events-none text-center">
                      {currentSub.speaker && <div className="text-[var(--color-gold-400)] text-[2.2vh] font-mono mb-1" style={{ textShadow: "0 2px 6px #000" }}>{currentSub.speaker} (V.O.)</div>}
                      <div className="inline-block px-4 py-1.5 rounded-lg bg-black/55 text-white text-[2.8vh] leading-snug" style={{ textShadow: "0 2px 8px #000" }}>{currentSub.text}</div>
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* transport + inspector + timeline */}
          {edl && videoClips.length > 0 && (
            <div className="px-6 pb-4 pt-2 space-y-2">
              <div className="flex items-center gap-3">
                <button onClick={() => engineRef.current?.toggle()} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white">{playing ? <Pause size={16} /> : <Play size={16} />}</button>
                <span className="text-[11px] font-mono text-white/60 w-24">{fmt(playhead)} / {fmt(duration)}</span>
                <input type="range" min={0} max={duration} step={0.05} value={Math.min(playhead, duration)} onChange={(e) => engineRef.current?.seek(parseFloat(e.target.value))} className="flex-1 accent-[var(--color-gold-500)]" />
                <button onClick={() => { if (engineRef.current) engineRef.current.loop = !engineRef.current.loop; }} title="Loop" className="p-2 rounded-full text-[var(--color-gold-400)]"><Repeat size={15} /></button>
                <div className="w-px h-5 bg-white/10" />
                <button onClick={splitAtPlayhead} title="Split shot at playhead (B)" className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10"><Scissors size={15} /></button>
                <button onClick={undo} disabled={!past.length} title="Undo (⌘Z)" className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30"><Undo2 size={15} /></button>
                <button onClick={redo} disabled={!future.length} title="Redo (⌘⇧Z)" className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30"><Redo2 size={15} /></button>
                <div className="w-px h-5 bg-white/10" />
                <label className="flex items-center gap-1 text-white/50" title="Master volume">
                  <Volume2 size={14} />
                  <input type="range" min={0} max={1.5} step={0.05} value={edl?.master_gain ?? 1} onChange={(e) => setMasterGain(parseFloat(e.target.value))} className="w-16 accent-[var(--color-gold-500)]" />
                </label>
              </div>

              {selectedClip ? (
                selectedClip.trackKind === "overlay"
                  ? <OverlayInspector clip={selectedClip.clip} trackId={selectedClip.trackId} onChange={changeClip} onRemove={removeClip} />
                  : <ClipInspector clip={selectedClip.clip} trackId={selectedClip.trackId} onChange={changeClip} onRemove={removeClip} />
              ) : lead && (lead.mediaType === "image" || lead.mediaType === "video") ? (
                <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 border border-sky-500/20 text-[11px] text-white/70">
                  {lead.mediaType === "video" ? <Film size={14} className="text-violet-300" /> : <ImagePlus size={14} className="text-sky-300" />}
                  <span className="font-semibold text-white truncate max-w-[28%]">{lead.label || (lead.mediaType === "video" ? "Inserted video" : "Inserted image")}</span>
                  <label className="flex items-center gap-1.5">{lead.mediaType === "video" ? "play" : "hold"}
                    <input type="number" min={0.3} max={120} step={0.5}
                      value={Number(selAssetDur.toFixed(1))}
                      onChange={(e) => setVisualDuration(selectedShot.group_key, parseFloat(e.target.value))}
                      className="w-14 bg-black/40 border border-white/10 rounded px-1 py-0.5" /> s
                  </label>
                  {lead.mediaType === "video" && (
                    <label className="flex items-center gap-1">speed
                      <select value={lead.speed || 1} onChange={(e) => setShotSpeed(selectedShot.group_key, parseFloat(e.target.value))}
                        className="bg-black/40 border border-white/10 rounded px-1 py-0.5 text-white">
                        {[0.25, 0.5, 1, 1.5, 2, 4].map((s) => <option key={s} value={s}>{s}×</option>)}
                      </select>
                    </label>
                  )}
                  {lead.mediaType === "video" && (
                    <button onClick={() => { setVfxTarget({ groupKey: selectedShot.group_key, plateUrl: lead.src, frameId: lead.frame_id, seconds: selAssetDur }); setVfxPrompt(""); setVfxMode("look"); }}
                      title="AI VFX: transform this clip with a prompt"
                      className="flex items-center gap-1 text-emerald-300 hover:text-emerald-200"><Wand2 size={12} /> VFX</button>
                  )}
                  <button onClick={() => removeShot(selectedShot.group_key)} className="ml-auto flex items-center gap-1 text-white/50 hover:text-red-400"><Eraser size={12} /> Remove</button>
                </div>
              ) : lead ? (
                <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-xs font-bold text-[#f5c453]">#{lead.authored_shot_number ?? lead.frame_number}</span>
                  {lead.timecode && <span className="text-[11px] text-white/60">{lead.timecode}</span>}
                  {selectedShot?.vo_text && <span className="text-[11px] text-white/40 italic truncate max-w-[30%]">“{selectedShot.vo_text}”</span>}
                  <div className="flex gap-1">
                    {MARK_BUTTONS.map(({ status, label, Icon }) => {
                      const on = (selMark.review_status ?? null) === status;
                      return (
                        <button key={label} onClick={() => saveMark(lead.frame_id, { review_status: status, director_notes: selMark.director_notes || null })}
                          className="text-[10px] px-2 py-1 rounded border flex items-center gap-1"
                          style={on && status ? { borderColor: MARK_COLORS[status], color: MARK_COLORS[status] } : { borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}>
                          <Icon size={11} /> {label}
                        </button>
                      );
                    })}
                  </div>
                  {animating.has(lead.frame_id) ? (
                    <span className="flex items-center gap-1 text-[10px] text-violet-300"><Loader2 size={11} className="animate-spin" /> Animating…</span>
                  ) : (
                    <button onClick={() => handleAnimateShot(lead.frame_id)} title="Animate this shot into a moving clip (Google Veo)"
                      className={`text-[10px] px-2 py-1 rounded border flex items-center gap-1 ${animatedFrames.has(lead.frame_id) ? "border-violet-500/50 text-violet-300" : "border-white/15 text-white/60 hover:text-violet-300"}`}>
                      <Clapperboard size={11} /> {animatedFrames.has(lead.frame_id) ? "Animated" : "Animate"}
                    </button>
                  )}
                  <input key={lead.frame_id} defaultValue={selMark.director_notes || ""} placeholder="Director note…"
                    onBlur={(e) => saveMark(lead.frame_id, { review_status: selMark.review_status ?? null, director_notes: e.target.value || null })}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white placeholder-white/30" />
                </div>
              ) : null}

              <MultiTrackTimeline edl={edl} engine={engineRef.current} fps={fps} playhead={playhead} shots={reel.shots} marks={marks}
                onSeek={(t) => engineRef.current?.seek(t)} onSelectShot={handleSelectShot} selectedShotKey={selectedShot?.group_key}
                selectedClipId={selectedClipId} onSelectClip={(id) => { setSelectedClipId(id); setSelectedShot(null); }}
                onChangeClip={changeClip} onChangeTrack={changeTrack} onReorderShots={reorderShots} onDropMedia={addVisualAtTime} onTrimVideo={trimVideoGroup}
                onRemoveLayer={removeLayer} onDropToLayer={addToLayer} onPickTransition={(gk) => setTransitionTarget(gk)}
                onAddVideoTrack={addVideoLayer} onAddAudioTrack={addEmptyAudioTrack} />
            </div>
          )}

          {/* AI music generator */}
          {musicOpen && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => !musicBusy && setMusicOpen(false)}>
              <div onClick={(e) => e.stopPropagation()} className="w-[min(92vw,440px)] rounded-2xl border border-white/10 bg-[#15130f] p-5 space-y-3 shadow-2xl">
                <div className="flex items-center gap-2 text-fuchsia-300"><Sparkles size={16} /><h3 className="text-sm font-semibold text-white">Generate music</h3></div>
                <p className="text-[11px] text-white/50">Describe the score. Lyria returns an instrumental ~30s clip you can trim, fade and duck under the voice-over.</p>
                <textarea autoFocus value={musicPrompt} onChange={(e) => setMusicPrompt(e.target.value)} rows={3}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleGenerateMusic(); }}
                  placeholder="warm cinematic piano and strings, hopeful, slowly building, no drums"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[12px] text-white placeholder-white/30 resize-none focus:outline-none focus:border-fuchsia-500/50" />
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-white/50">Quality</span>
                  {[["clip", "Clip · fast"], ["pro", "Pro · richer"]].map(([q, label]) => (
                    <button key={q} onClick={() => setMusicQuality(q)} className={`px-2 py-1 rounded border ${musicQuality === q ? "border-fuchsia-500/60 text-fuchsia-200" : "border-white/10 text-white/50"}`}>{label}</button>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button disabled={musicBusy} onClick={() => setMusicOpen(false)} className="px-3 py-1.5 text-[12px] text-white/60 hover:text-white disabled:opacity-40">Cancel</button>
                  <button disabled={musicBusy || !musicPrompt.trim()} onClick={handleGenerateMusic}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fuchsia-500/20 border border-fuchsia-500/50 text-fuchsia-200 text-[12px] disabled:opacity-40">
                    {musicBusy ? <><Loader2 size={13} className="animate-spin" /> Composing…</> : <><Sparkles size={13} /> Generate</>}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* AI VFX modal */}
          {vfxTarget && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => !vfxBusy && setVfxTarget(null)}>
              <div onClick={(e) => e.stopPropagation()} className="w-[min(92vw,460px)] rounded-2xl border border-white/10 bg-[#15130f] p-5 space-y-3 shadow-2xl">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-emerald-300"><Wand2 size={16} /><h3 className="text-sm font-semibold text-white">AI VFX</h3></div>
                  {vfxModel && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/30 text-emerald-200/90 whitespace-nowrap">{vfxModel.label} · ${vfxModel.cost_per_second}/s</span>
                  )}
                </div>
                <p className="text-[11px] text-white/50">Transform this clip with a prompt — it keeps the motion &amp; composition; the result drops back onto the timeline.{vfxModel?.min_seconds ? ` Best on a ${vfxModel.min_seconds}–${vfxModel.max_seconds}s clip.` : ""}</p>
                {vfxModels.length > 1 && (
                  <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className="text-white/50">Model</span>
                    {vfxModels.map((p) => (
                      <button key={p.name} disabled={!p.available}
                        onClick={() => p.available && setVfxModel(p)}
                        title={p.available ? p.blurb : `Needs ${p.requires_env}`}
                        className={`px-2 py-1 rounded border ${vfxModel?.name === p.name ? "border-emerald-500/60 text-emerald-200" : "border-white/10 text-white/50"} ${!p.available ? "opacity-40 cursor-not-allowed" : ""}`}>
                        {p.label}{!p.available ? " · no key" : ""}
                      </button>
                    ))}
                  </div>
                )}
                {vfxModel && vfxTarget?.seconds != null && (vfxTarget.seconds < vfxModel.min_seconds || vfxTarget.seconds > vfxModel.max_seconds) && (
                  <p className="text-[11px] text-amber-300/90">This clip is {vfxTarget.seconds.toFixed(1)}s — {vfxModel.label} needs {vfxModel.min_seconds}–{vfxModel.max_seconds}s, so it may be rejected.</p>
                )}
                {/* What to change — layman icon tiles + a guided, subject-preserving prompt
                    (or "Free write" for a raw, unwrapped prompt). */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-white/50">What do you want to change?</span>
                    {vfxMode !== "free" && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-200/80">Keeping: your subject + motion</span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {VFX_MODES.map((m) => {
                      const on = vfxMode === m.key; const Icon = m.Icon;
                      return (
                        <button key={m.key} onClick={() => setVfxMode(m.key)}
                          className={`flex flex-col items-center gap-1 px-1 py-2.5 rounded-lg border text-[10px] leading-tight ${on ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200" : "border-white/10 text-white/55 hover:border-white/25"}`}>
                          <Icon size={16} /> {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <textarea autoFocus value={vfxPrompt} onChange={(e) => setVfxPrompt(e.target.value)} rows={2}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleStartVfx(); }}
                  placeholder={vfxDef.ph}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[12px] text-white placeholder-white/30 resize-none focus:outline-none focus:border-emerald-500/50" />
                {vfxMode === "free" ? (
                  <p className="text-[10px] text-amber-300/70 leading-snug">Raw mode — your prompt is sent <span className="text-amber-200">exactly as typed</span>. Describe the change, not the subject, or it may be regenerated.</p>
                ) : (
                  <p className="text-[10px] text-white/40 leading-snug">Sends to the model: <span className="text-white/60">{vfxDef.pre}<span className="text-emerald-300/90">{vfxPrompt.trim() || "…"}</span>{vfxDef.suf}</span></p>
                )}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-[10px] text-white/40">{vfxModel && vfxTarget?.seconds != null ? `~$${(vfxModel.cost_per_second * vfxTarget.seconds).toFixed(2)} est.` : ""}</span>
                  <div className="flex items-center gap-2">
                    <button disabled={vfxBusy} onClick={() => setVfxTarget(null)} className="px-3 py-1.5 text-[12px] text-white/60 hover:text-white disabled:opacity-40">Cancel</button>
                    <button disabled={vfxBusy || !vfxPrompt.trim()} onClick={handleStartVfx}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 text-[12px] disabled:opacity-40">
                      {vfxBusy ? <><Loader2 size={13} className="animate-spin" /> Rendering…</> : <><Wand2 size={13} /> Transform</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ExportsPanel open={exportsOpen} onClose={() => setExportsOpen(false)} projectId={projectId} refreshKey={exportsRefresh} />
          <MediaPool open={binOpen} onClose={() => setBinOpen(false)} projectId={projectId}
            onUploadClick={() => imageInputRef.current?.click()}
            onInsert={(a) => addVisualFromAsset(a, playhead)} />

          {/* Transition picker (the transition ENTERING the selected shot) */}
          {transitionTarget && (() => {
            const vt = edl?.tracks?.find((t) => t.kind === "video");
            const first = vt?.clips.find((c) => (c.group_key || c.frame_id) === transitionTarget);
            const cur = first?.transition_in || null;
            const dur = cur?.duration || 0.6;
            return (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setTransitionTarget(null)}>
                <div onClick={(e) => e.stopPropagation()} className="w-[min(94vw,520px)] rounded-2xl border border-white/10 bg-[#15130f] p-5 space-y-3 shadow-2xl">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white">Transition into this shot</h3>
                    <span className="text-[10px] text-white/40">renders in the exported MP4</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 max-h-[42vh] overflow-y-auto">
                    <button onClick={() => { setTransition(transitionTarget, null); setTransitionTarget(null); }}
                      className={`px-2 py-2 rounded border text-[10px] ${!cur ? "border-emerald-500/60 text-emerald-200" : "border-white/10 text-white/55 hover:border-white/25"}`}>None (cut)</button>
                    {TRANSITIONS.map((tr) => (
                      <button key={tr.type} onClick={() => { setTransition(transitionTarget, { type: tr.type, duration: dur }); setTransitionTarget(null); }}
                        className={`px-2 py-2 rounded border text-[10px] ${cur?.type === tr.type ? "border-emerald-500/60 text-emerald-200" : "border-white/10 text-white/55 hover:border-white/25"}`}>{tr.label}</button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-white/60">
                    <span>Length</span>
                    <input type="range" min={0.2} max={2} step={0.1} defaultValue={dur}
                      onChange={(e) => { if (cur) setTransition(transitionTarget, { type: cur.type, duration: parseFloat(e.target.value) }); }}
                      className="flex-1 accent-[var(--color-gold-500)]" />
                    <span>{dur.toFixed(1)}s</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
